// src/services/storageService.js — user data storage with support for
// local, cloud, or both.
//
// The cloud half is the auth hub's per-user thin layer
// (users/{uid}/apps/profound-philosophy via hubAppState.js) — NOT the app's
// own Firestore. Public APIs are unchanged from the app-Firestore era:
// callers still pass (userId, data, storagePreference), though identity for
// cloud writes is derived server-side from the hub session.
//
// Shape of the hub document:
//   assessmentResults : object
//   moduleProgress    : { [moduleId]: progress }
//   toolData          : { [toolId]: data }
//   toolUsage         : { [toolId]: usage }
//   gamification      : object (written by the gamification store)
//
// Australian English throughout.

import {
  hydrateAppState,
  resetHydrateCache,
  queuePatch,
  savePatchNow,
  hubSyncAvailable,
} from './hubAppState';

const wantsLocal = (pref) => pref === 'local' || pref === 'both';
const wantsCloud = (pref) => pref === 'cloud' || pref === 'both';

// ============================================================================
// ASSESSMENT STORAGE
// ============================================================================

export const saveAssessmentResults = async (userId, assessmentData, storagePreference = 'both') => {
  try {
    if (wantsLocal(storagePreference)) {
      localStorage.setItem('profoundPhilosophy_assessmentResults', JSON.stringify(assessmentData));
    }
    if (wantsCloud(storagePreference) && userId) {
      queuePatch({ assessmentResults: { ...assessmentData, savedAt: new Date().toISOString() } });
    }
    return true;
  } catch (error) {
    console.error('❌ Error saving assessment results:', error);
    return false;
  }
};

export const loadAssessmentResults = async (userId, storagePreference = 'both') => {
  try {
    if (wantsCloud(storagePreference) && userId) {
      const hubDoc = await hydrateAppState();
      if (hubDoc?.assessmentResults) {
        if (storagePreference === 'both') {
          localStorage.setItem('profoundPhilosophy_assessmentResults', JSON.stringify(hubDoc.assessmentResults));
        }
        return hubDoc.assessmentResults;
      }
    }
    if (wantsLocal(storagePreference)) {
      const localData = localStorage.getItem('profoundPhilosophy_assessmentResults');
      if (localData) return JSON.parse(localData);
    }
    return null;
  } catch (error) {
    console.error('❌ Error loading assessment results:', error);
    const localData = localStorage.getItem('profoundPhilosophy_assessmentResults');
    return localData ? JSON.parse(localData) : null;
  }
};

// ============================================================================
// MODULE PROGRESS STORAGE
// ============================================================================

export const saveModuleProgress = async (userId, moduleId, progressData, storagePreference = 'both') => {
  try {
    if (wantsLocal(storagePreference)) {
      const allProgress = JSON.parse(localStorage.getItem('profoundPhilosophy_moduleProgress') || '{}');
      allProgress[moduleId] = {
        ...progressData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('profoundPhilosophy_moduleProgress', JSON.stringify(allProgress));
    }
    if (wantsCloud(storagePreference) && userId) {
      queuePatch({ moduleProgress: { [moduleId]: { ...progressData, savedAt: new Date().toISOString() } } });
    }
    return true;
  } catch (error) {
    console.error(`❌ Error saving module ${moduleId} progress:`, error);
    return false;
  }
};

export const loadModuleProgress = async (userId, storagePreference = 'both') => {
  try {
    if (wantsCloud(storagePreference) && userId) {
      const hubDoc = await hydrateAppState();
      const progress = hubDoc?.moduleProgress;
      if (progress && Object.keys(progress).length > 0) {
        if (storagePreference === 'both') {
          localStorage.setItem('profoundPhilosophy_moduleProgress', JSON.stringify(progress));
        }
        return progress;
      }
    }
    if (wantsLocal(storagePreference)) {
      const localData = localStorage.getItem('profoundPhilosophy_moduleProgress');
      if (localData) return JSON.parse(localData);
    }
    return {};
  } catch (error) {
    console.error('❌ Error loading module progress:', error);
    const localData = localStorage.getItem('profoundPhilosophy_moduleProgress');
    return localData ? JSON.parse(localData) : {};
  }
};

// ============================================================================
// TOOL DATA STORAGE
// ============================================================================

export const saveToolData = async (userId, toolId, toolData, storagePreference = 'both') => {
  try {
    if (wantsLocal(storagePreference)) {
      const allToolData = JSON.parse(localStorage.getItem('profoundPhilosophy_toolData') || '{}');
      allToolData[toolId] = {
        ...toolData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('profoundPhilosophy_toolData', JSON.stringify(allToolData));
    }
    if (wantsCloud(storagePreference) && userId) {
      queuePatch({ toolData: { [toolId]: { ...toolData, savedAt: new Date().toISOString() } } });
    }
    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} data:`, error);
    return false;
  }
};

export const loadToolData = async (userId, storagePreference = 'both') => {
  try {
    if (wantsCloud(storagePreference) && userId) {
      const hubDoc = await hydrateAppState();
      const toolData = hubDoc?.toolData;
      if (toolData && Object.keys(toolData).length > 0) {
        if (storagePreference === 'both') {
          localStorage.setItem('profoundPhilosophy_toolData', JSON.stringify(toolData));
        }
        return toolData;
      }
    }
    if (wantsLocal(storagePreference)) {
      const localData = localStorage.getItem('profoundPhilosophy_toolData');
      if (localData) return JSON.parse(localData);
    }
    return {};
  } catch (error) {
    console.error('❌ Error loading tool data:', error);
    const localData = localStorage.getItem('profoundPhilosophy_toolData');
    return localData ? JSON.parse(localData) : {};
  }
};

export const saveToolUsage = async (userId, toolId, usageData, storagePreference = 'both') => {
  try {
    if (wantsLocal(storagePreference)) {
      const allUsage = JSON.parse(localStorage.getItem('profoundPhilosophy_toolUsage') || '{}');
      allUsage[toolId] = {
        ...usageData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('profoundPhilosophy_toolUsage', JSON.stringify(allUsage));
    }
    if (wantsCloud(storagePreference) && userId) {
      queuePatch({ toolUsage: { [toolId]: { ...usageData, savedAt: new Date().toISOString() } } });
    }
    return true;
  } catch (error) {
    console.error(`❌ Error saving tool ${toolId} usage:`, error);
    return false;
  }
};

export const loadToolUsage = async (userId, storagePreference = 'both') => {
  try {
    if (wantsCloud(storagePreference) && userId) {
      const hubDoc = await hydrateAppState();
      const usage = hubDoc?.toolUsage;
      if (usage && Object.keys(usage).length > 0) {
        if (storagePreference === 'both') {
          localStorage.setItem('profoundPhilosophy_toolUsage', JSON.stringify(usage));
        }
        return usage;
      }
    }
    if (wantsLocal(storagePreference)) {
      const localData = localStorage.getItem('profoundPhilosophy_toolUsage');
      if (localData) return JSON.parse(localData);
    }
    return {};
  } catch (error) {
    console.error('❌ Error loading tool usage:', error);
    const localData = localStorage.getItem('profoundPhilosophy_toolUsage');
    return localData ? JSON.parse(localData) : {};
  }
};

// ============================================================================
// DATA LOADING & MIGRATION
// ============================================================================

const DEFAULT_GAMIFICATION = () => ({
  totalPoints: 0,
  unlockedAchievements: [],
  streaks: {
    currentLoginStreak: 0,
    longestLoginStreak: 0,
    lastLoginDate: null,
    weeklyChallengeStreak: 0
  },
  lastPointsAwarded: []
});

export const loadAllUserData = async (userId, storagePreference = 'both') => {
  try {
    console.log('📥 Loading all user data...');

    // One hub read serves every section below (shared hydrate cache).
    const [assessment, moduleProgress, toolData, toolUsage, gamification] = await Promise.all([
      loadAssessmentResults(userId, storagePreference),
      loadModuleProgress(userId, storagePreference),
      loadToolData(userId, storagePreference),
      loadToolUsage(userId, storagePreference),
      loadGamificationData(userId, storagePreference)
    ]);

    console.log('✅ All user data loaded successfully');

    return {
      assessmentResults: assessment,
      moduleProgress: moduleProgress,
      toolData: toolData,
      toolUsage: toolUsage,
      gamification: gamification
    };
  } catch (error) {
    console.error('❌ Error loading all user data:', error);
    return {
      assessmentResults: null,
      moduleProgress: {},
      toolData: {},
      toolUsage: {},
      gamification: DEFAULT_GAMIFICATION()
    };
  }
};

// ============================================================================
// GAMIFICATION STORAGE
// ============================================================================

export const saveGamificationData = async (userId, gamificationData, storagePreference = 'both') => {
  try {
    if (wantsLocal(storagePreference)) {
      localStorage.setItem('profoundPhilosophy_gamification', JSON.stringify(gamificationData));
    }
    if (wantsCloud(storagePreference) && userId) {
      queuePatch({ gamification: { ...gamificationData, savedAt: new Date().toISOString() } });
    }
    return true;
  } catch (error) {
    console.error('❌ Error saving gamification data:', error);
    return false;
  }
};

export const loadGamificationData = async (userId, storagePreference = 'both') => {
  try {
    if (wantsCloud(storagePreference) && userId) {
      const hubDoc = await hydrateAppState();
      if (hubDoc?.gamification) {
        if (storagePreference === 'both') {
          localStorage.setItem('profoundPhilosophy_gamification', JSON.stringify(hubDoc.gamification));
        }
        return hubDoc.gamification;
      }
    }
    if (wantsLocal(storagePreference)) {
      const localData = localStorage.getItem('profoundPhilosophy_gamification');
      if (localData) return JSON.parse(localData);
    }
    return DEFAULT_GAMIFICATION();
  } catch (error) {
    console.error('❌ Error loading gamification data:', error);
    const localData = localStorage.getItem('profoundPhilosophy_gamification');
    return localData ? JSON.parse(localData) : DEFAULT_GAMIFICATION();
  }
};

// ============================================================================
// DATA CLEANUP
// ============================================================================

// The toolkit's sixteen tools write to their own un-namespaced keys; a reset
// that missed them would leave a user's most personal reflections (ethics
// audits, political leanings, existential confessions) on the device.
export const TOOL_STORAGE_KEYS = [
  'socraticDialogues', 'builtArguments', 'fallacyDetectiveStats',
  'assumptionExcavations', 'credibleEvaluations', 'epistemicCalibrations',
  'ethicalAnalyses', 'ethicsAudits', 'identityReflections', 'freeWillEntries',
  'chineseRoomInteractive', 'qualiaEntries', 'politicalMappings',
  'veilAnalyses', 'existentialAnalyses', 'meaningCreationWorkshop',
  'philosophicalProfileResults', 'philosophicalPositions', 'philosophyJournal',
  // Written by the saved-entries rail and the Fallacy Detective since the
  // list was first made (found in the October 2026 audit).
  'chineseRoomSessions', 'meaningWorkshops', 'fallacySeenExamples',
  // The points, badges and streak store (zustand persist).
  'profound-philosophy-gamification'
];

export const clearLocalData = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('profoundPhilosophy_')) {
        localStorage.removeItem(key);
      }
    });
    // The module the reader last took to the companion (tab-local).
    try { sessionStorage.removeItem('profoundPhilosophy_companionModule'); } catch { /* nothing to do */ }
    TOOL_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
    console.log('✅ Local data cleared');
    return true;
  } catch (error) {
    console.error('❌ Error clearing local data:', error);
    return false;
  }
};

export const clearCloudData = async (userId) => {
  if (!userId) {
    console.error('No user ID provided for cloud data clearing');
    return false;
  }
  if (!hubSyncAvailable()) return false;

  try {
    // Null out every section of the hub thin-layer document. The server
    // merge-writes, so nulls tombstone the sections.
    const cleared = await savePatchNow({
      assessmentResults: null,
      moduleProgress: null,
      toolData: null,
      toolUsage: null,
      gamification: null
    });
    resetHydrateCache();
    if (cleared) console.log('✅ Cloud data cleared');
    return cleared;
  } catch (error) {
    console.error('❌ Error clearing cloud data:', error);
    return false;
  }
};

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

export const migrateOldLocalStorage = () => {
  // Profound Philosophy has no legacy key generations to migrate — the app
  // launched with the profoundPhilosophy_ prefix. Kept as a no-op so the
  // AppContext load path stays identical across the DDD educational apps.
  // (No legacy app-Firestore import either: the app's own auth pool was
  // verified empty before the hub cutover, so there is nothing to carry.)
  return 0;
};
